// src/elements.ts
import { defineCustomElement } from "@aria-ui/core";

// src/tooltip-content.setup.ts
import {
  createSignal,
  useAriaAttribute,
  useAttribute,
  useEffect
} from "@aria-ui/core";
import { useOverlayPositioner } from "@aria-ui/overlay/elements";
import { usePresence } from "@aria-ui/presence";

// src/tooltip.context.ts
import { createContext } from "@aria-ui/core";
var hoveringContext = createContext("tooltip/hovering", false);
var focusedContext = createContext("tooltip/focused", false);
var openContext = createContext("tooltip/open", false);
var idContext = createContext("tooltip/id", null);

// src/tooltip-content.setup.ts
function useTooltipContent(element, { state, emit }) {
  const open = openContext.consume(element);
  const id = idContext.consume(element);
  useAriaAttribute(element, "aria-hidden", () => `${!open.get()}`);
  useAttribute(element, "id", () => id.get() || void 0);
  useAttribute(element, "data-state", () => open.get() ? "open" : "closed");
  const hoist = createSignal(state.hoist.peek());
  const visible = usePresence(element, open);
  useEffect(element, () => {
    hoist.set(visible.get() ? state.hoist.get() : false);
  });
  useOverlayPositioner(element, { state: { ...state, hoist }, emit });
}

// src/tooltip-content.types.ts
import {
  overlayPositionerEvents,
  overlayPositionerProps
} from "@aria-ui/overlay";
var tooltipContentProps = {
  ...overlayPositionerProps
};
var tooltipContentEvents = overlayPositionerEvents;

// src/tooltip-root.setup.ts
import {
  createComputed,
  createSignal as createSignal2,
  useEffect as useEffect2
} from "@aria-ui/core";
import { useOverlayRoot } from "@aria-ui/overlay/elements";
import { nanoid } from "nanoid";
var activatedAt = 0;
var deactivatedAt = 0;
var openingTooltip = null;
var openingId = null;
function useTooltipRoot(element, { state, emit }) {
  useOverlayRoot(element);
  const hovering = createSignal2(false);
  const focused = createSignal2(false);
  const activated = createComputed(() => hovering.get() || focused.get());
  const open = createSignal2(false);
  const id = nanoid();
  const updateOpen = (value) => {
    if (open.peek() === value) return;
    open.set(value);
    emit("openChange", value);
  };
  const check = () => {
    const now = Date.now();
    if (activated.peek()) {
      if (
        // The trigger has been activated for a while
        now - activatedAt >= state.openDelay.peek() || // The previous opened tooltip has just been closed
        openingId && now - deactivatedAt <= state.closeDelay.peek()
      ) {
        updateOpen(true);
        if (openingTooltip && openingId !== id) {
          openingTooltip.set(false);
        }
        openingTooltip = open;
        openingId = id;
      }
    } else {
      if (now - deactivatedAt >= state.closeDelay.peek()) {
        updateOpen(false);
        if (openingId === id) {
          openingTooltip = null;
          openingId = null;
        }
      }
    }
  };
  useEffect2(element, () => {
    if (activated.get()) {
      activatedAt = Date.now();
    } else {
      deactivatedAt = Date.now();
    }
    for (const delay of /* @__PURE__ */ new Set([
      0,
      state.openDelay.peek(),
      state.closeDelay.peek()
    ])) {
      setTimeout(check, delay);
    }
  });
  hoveringContext.provide(element, hovering);
  focusedContext.provide(element, focused);
  openContext.provide(
    element,
    createComputed(() => open.get())
  );
  idContext.provide(element, createSignal2(id));
}

// src/tooltip-root.types.ts
var tooltipRootProps = {
  openDelay: { default: 700 },
  closeDelay: { default: 300 },
  open: { default: false }
};
var tooltipRootEvents = {
  openChange: {}
};

// src/tooltip-trigger.setup.ts
import {
  useAriaAttribute as useAriaAttribute2,
  useEventListener
} from "@aria-ui/core";
import { useOverlayAnchor } from "@aria-ui/overlay/elements";
function useTooltipTrigger(element) {
  useOverlayAnchor(element);
  const hovering = hoveringContext.consume(element);
  const focused = focusedContext.consume(element);
  const open = openContext.consume(element);
  const id = idContext.consume(element);
  useEventListener(element, "pointerenter", () => {
    hovering.set(true);
  });
  useEventListener(element, "pointerleave", () => {
    hovering.set(false);
  });
  useEventListener(element, "focusin", () => {
    focused.set(true);
  });
  useEventListener(element, "focusout", () => {
    focused.set(false);
  });
  const handlePress = () => {
    hovering.set(false);
    focused.set(false);
  };
  useEventListener(element, "click", handlePress);
  useEventListener(element, "keydown", handlePress);
  useAriaAttribute2(element, "aria-describedby", () => {
    const openValue = open.get();
    const idValue = id.get();
    return openValue && idValue ? idValue : void 0;
  });
}

// src/tooltip-trigger.types.ts
var tooltipTriggerProps = {};
var tooltipTriggerEvents = {};

// src/elements.ts
var TooltipContentElement = class extends defineCustomElement({
  props: tooltipContentProps,
  events: tooltipContentEvents,
  setup: useTooltipContent
}) {
};
var TooltipRootElement = class extends defineCustomElement({
  props: tooltipRootProps,
  events: tooltipRootEvents,
  setup: useTooltipRoot
}) {
};
var TooltipTriggerElement = class extends defineCustomElement({
  props: tooltipTriggerProps,
  events: tooltipTriggerEvents,
  setup: useTooltipTrigger
}) {
};

export {
  useTooltipContent,
  tooltipContentProps,
  tooltipContentEvents,
  useTooltipRoot,
  tooltipRootProps,
  tooltipRootEvents,
  useTooltipTrigger,
  tooltipTriggerProps,
  tooltipTriggerEvents,
  TooltipContentElement,
  TooltipRootElement,
  TooltipTriggerElement
};
